import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useStaffTraining } from '../context/StaffTrainingContext';
import { HiOutlineUsers, HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { FiCheckCircle, FiClock, FiAlertCircle, FiFilter, FiPlus, FiArrowRight } from "react-icons/fi";
import DatePicker from '../../../components/DatePicker';

export default function StaffTrainingDashboard() {

  const { data, loading, selectedDate, setSelectedDate, setPanel, canEdit } = useStaffTraining();
  const navigate = useNavigate();
  const summary = data?.summary;
  const metric = key => summary ? summary[key] : loading ? '…' : '—';
  const percentage = key => summary?.activeStaff ? Math.round(summary[key] / summary.activeStaff * 100) + '%' : '0%';

  const cards = [
    {
      id: 1,
      title: "Active Staff",
      value: metric('activeStaff'),
      subtext: "Team members",
      icon: HiOutlineUsers,
      iconBg: "bg-blue-600",
      iconColor: "text-white",
    },
    {
      id: 2,
      title: "Up to Date",
      value: metric('upToDate'),
      percentage: percentage('upToDate'),
      subtext: summary?.recorded ? `${summary.recorded} recorded without expiry` : 'All required training',
      icon: FiCheckCircle,
      iconBg: "bg-emerald-600",
      iconColor: "text-white",
    },
    {
      id: 3,
      title: "Due Soon (≤ 30 days)",
      value: metric('dueSoon'),
      percentage: percentage('dueSoon'),
      subtext: "Needs attention",
      icon: FiClock,
      iconBg: "bg-amber-500",
      iconColor: "text-white",
    },
    {
      id: 4,
      title: "Overdue",
      value: metric('overdue'),
      percentage: percentage('overdue'),
      subtext: "Action required",
      icon: FiAlertCircle,
      iconBg: "bg-red-600",
      iconColor: "text-white",
      hasDot: true,
    },
    {
      id: 5,
      title: "Training Gaps",
      value: metric('trainingGaps'),
      subtext: "Open actions",
      icon: HiOutlineClipboardDocumentList,
      iconBg: "bg-white border-2 border-blue-600",
      iconColor: "text-blue-600",
      link: "View details",
    },
  ];

  return (
    <div className="w-full min-h-full bg-slate-50 p-4 sm:p-5 lg:p-6 font-sans">
      {/* =========================================================
          HEADER
      ========================================================= */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        {/* TITLE */}
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Staff Training Overview
          </h1>

          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Track training compliance, identify gaps and keep your team
            inspection-ready.
          </p>
        </div>

        {/* HEADER CONTROLS */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto">

          <DatePicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />

          {/* FILTER BUTTON */}
          <button
            type="button"
            onClick={() => setPanel('filters')}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
          >
            <FiFilter className="text-slate-400 w-3.5 h-3.5 shrink-0" />

            <span>Filters</span>
          </button>

          {/* ADD TRAINING BUTTON */}
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => navigate('/staff-training/records/create')}
            className="flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium shadow-sm transition-colors whitespace-nowrap"
          >
            <FiPlus className="w-3.5 h-3.5 stroke-[3] shrink-0" />

            <span>Add Training / Record</span>
          </button>
        </div>
      </div>

      {/* =========================================================
          SUMMARY CARDS
      ========================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <div
              key={card.id}
              className="relative bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex flex-col justify-between min-h-[125px]"
            >
              {/* CARD TOP */}
              <div className="flex items-center gap-3 min-w-0">
                {/* ICON */}
                <div
                  className={`p-2 rounded-lg shrink-0 flex items-center justify-center w-9 h-9 ${card.iconBg}`}
                >
                  <Icon
                    className={`w-5 h-5 ${card.iconColor}`}
                  />
                </div>

                {/* TITLE */}
                <span className="text-xs font-bold text-slate-800 leading-tight">
                  {card.title}
                </span>
              </div>

              {/* CARD METRICS */}
              <div className="mt-4">
                {/* VALUE */}
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-slate-900 leading-none">
                    {card.value}
                  </span>

                  {card.percentage && (
                    <span className="text-[10px] font-medium text-slate-400">
                      {card.percentage}
                    </span>
                  )}
                </div>

                {/* SUBTEXT / LINK */}
                <div className="flex items-center justify-between gap-2 mt-1 text-[11px] text-slate-400 tracking-wide">
                  <span>{card.subtext}</span>

                  {card.link && (
                    <button
                      type="button"
                      onClick={() => setPanel('actions')}
                      className="inline-flex items-center gap-0.5 text-blue-600 font-semibold hover:underline whitespace-nowrap"
                    >
                      <span>{card.link}</span>

                      <FiArrowRight className="w-3 h-3 shrink-0" />
                    </button>
                  )}
                </div>
              </div>

              {/* OVERDUE INDICATOR */}
              {card.hasDot && (
                <div className="absolute bottom-3 right-6 w-1.5 h-1.5 bg-red-500 rounded-full" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
